/**
 * PageHeader Component
 *
 * Consistent page header with optional eyebrow, title, description, and actions.
 * When `eyebrow` is set, uses the modern 3-line Taskify layout (red label → title → subtitle).
 */

import React, { ReactNode } from 'react';
import { LucideIcon } from 'lucide-react';
import { Button } from '../Button';

export interface PageHeaderProps {
  /** Page title */
  title: string;
  /** Optional description */
  description?: string;
  /** Red eyebrow label above the title (enables modern 3-line layout) */
  eyebrow?: string;
  /** Optional icon — in modern layout shown in the eyebrow row; legacy layout inline with title */
  icon?: LucideIcon;
  /** Icon color for legacy layout (e.g., 'text-blue-500') */
  iconColor?: string;
  /** Content rendered before the title block (e.g. back button) */
  leading?: ReactNode;
  /** Add button text (if provided, shows add button) */
  addButtonText?: string;
  /** Add button click handler */
  onAdd?: () => void;
  /** Custom header actions */
  actions?: ReactNode;
  /** Count badge (used by DataTable) */
  countBadge?: { count: number; label?: string };
  /** Optional content centered between title and badge */
  headerCenter?: ReactNode;
  /** Accent color for count badge styling */
  accentColor?: string;
  /** Additional className on the outer wrapper */
  className?: string;
}

export const PageHeader: React.FC<PageHeaderProps> = ({
  title,
  description,
  eyebrow,
  icon: Icon,
  iconColor = 'text-primary-500',
  leading,
  addButtonText,
  onAdd,
  actions,
  countBadge,
  headerCenter,
  accentColor = '#c00035',
  className = '',
}) => {
  const actionSlot =
    actions || (addButtonText && onAdd) ? (
      <>
        {actions}
        {addButtonText && onAdd ? (
          <Button variant="primary" onClick={onAdd}>
            {addButtonText}
          </Button>
        ) : null}
      </>
    ) : null;

  if (eyebrow) {
    return (
      <div
        className={`flex flex-wrap items-start sm:items-center justify-between gap-3 ${className}`}
      >
        <div className="flex items-start gap-3 min-w-0 flex-1">
          {leading}
          <div className="min-w-0">
            <div className="flex items-center gap-2 text-sm font-medium text-red-600">
              {Icon ? <Icon size={18} /> : null}
              {eyebrow}
            </div>
            <h1 className="mt-1 text-2xl font-bold text-gray-900 truncate">
              {title}
            </h1>
            {description ? (
              <p className="text-sm text-gray-500">{description}</p>
            ) : null}
          </div>
        </div>

        {headerCenter ? (
          <div className="order-last w-full sm:order-none sm:w-auto sm:flex-1 sm:max-w-md sm:mx-auto">
            {headerCenter}
          </div>
        ) : null}

        {countBadge ? (
          <div className="flex-shrink-0">
            <div
              className="rounded-lg border px-3 py-1.5"
              style={{
                background: `linear-gradient(135deg, ${accentColor}10, ${accentColor}05)`,
                borderColor: `${accentColor}20`,
              }}
            >
              <span
                className="whitespace-nowrap text-xs font-semibold sm:text-sm"
                style={{ color: accentColor }}
              >
                {countBadge.count} {countBadge.label || 'items'}
              </span>
            </div>
          </div>
        ) : null}

        {actionSlot ? (
          <div className="flex flex-shrink-0 items-center gap-2">{actionSlot}</div>
        ) : null}
      </div>
    );
  }

  return (
    <div
      className={`flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between ${className}`}
    >
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-3">
          {leading}
          <div className="min-w-0">
            <h1 className="flex items-center gap-3 text-2xl font-bold text-gray-900">
              {Icon ? <Icon className={`h-6 w-6 ${iconColor}`} /> : null}
              <span className="truncate">{title}</span>
            </h1>
            {description ? (
              <p className="mt-1 text-sm text-gray-500 sm:text-base">
                {description}
              </p>
            ) : null}
          </div>
        </div>
      </div>

      {actionSlot ? (
        <div className="flex flex-shrink-0 items-center gap-2">{actionSlot}</div>
      ) : null}
    </div>
  );
};

export default PageHeader;
